<template>
    <div
        ref="selectionCardList"
        role="radiogroup"
        aria-labelledby="step-header-title"
        :class="{
            'cz2__4-grid': isKiosk && isAccessoriesStep,
            'cz2__scrollbar-margin': isKiosk && hasScrollbar,
            'cz2__kiosk-padding': isKiosk,
        }"
        class="cz2__scrollbar-hide selection-card-list"
    >
        <slot />
    </div>
</template>

<style lang="scss">
    @import '../../style/variables';

    #cz2.cz2 {
        .selection-card-list {
            overflow: auto;

            padding: var(--layout-spacing);
            padding-top: 24px;

            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: var(--layout-spacing);

            @include respond-above(md) {
                grid-template-columns: repeat(3, minmax(0, 1fr));
            }

            @include respond-above(lg) {
                padding-top: 32px;
            }

            &.cz2__4-grid {
                grid-template-columns: repeat(4, minmax(0, 1fr));
            }

            &.cz2__kiosk-padding {
                padding: calc(2 * var(--layout-spacing));
                padding-top: 24px;
            }
        }
    }

    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //   ▗▄▄▖▗▄▄▖ ▗▄▄▄▖▗▖   ▗▖   ▗▄▄▄▖▗▖  ▗▖ ▗▄▄▖    ▗▄▄▖ ▗▄▄▄▖▗▄▄▄▖
    //  ▐▌   ▐▌ ▐▌▐▌   ▐▌   ▐▌     █  ▐▛▚▖▐▌▐▌       ▐▌ ▐▌▐▌   ▐▌
    //   ▝▀▚▖▐▛▀▘ ▐▛▀▀▘▐▌   ▐▌     █  ▐▌ ▝▜▌▐▌▝▜▌    ▐▛▀▚▖▐▛▀▀▘▐▛▀▀▘
    //  ▗▄▄▞▘▐▌   ▐▙▄▄▖▐▙▄▄▖▐▙▄▄▖▗▄█▄▖▐▌  ▐▌▝▚▄▞▘    ▐▙▄▞▘▐▙▄▄▖▐▙▄▄
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #cz2.cz2.cz2__is-sb {
        .selection-card-list {
            padding-top: 12px;

            @include respond-above(lg) {
                padding-top: 32px;
            }
        }
    }
</style>

<script>
    import * as constants from '../../constants';

    export default {
        data() {
            return {
                focusedButtonIndex: 0,
                hasKeyEventListener: false,
            };
        },

        mounted() {
            this.focusOnFirstOption();
            this.updateTabIndexes();
            this.addKeyDownListener();
            document.addEventListener('focusin', this.focusChanged);
        },

        beforeDestroy() {
            this.removeKeyDownListener();
            document.removeEventListener('focusin', this.focusChanged);
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            currentGroup() {
                return this.$store.state.customizer.currentGroup;
            },

            editPlacement() {
                return this.currentGroup?.placements?.[0];
            },

            selectedComponent() {
                return this.viewController.selectedAtPlacement(this.editPlacement.code);
            },

            selectedButtonIndex() {
                const listWrapper = document.querySelector('.selection-card-list');
                const selectedButton = listWrapper.querySelector('.selection-card--selected');
                const index = selectedButton ? Array.prototype.indexOf.call(listWrapper.children, selectedButton) : 0;

                return index;
            },

            currentGroupCode() {
                return this.$store.state.customizer.currentGroup.code;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            isAccessoriesStep() {
                return constants.ACCESSORIES_STEPS.includes(this.currentGroupCode);
            },

            hasScrollbar() {
                return this.$store.state.hasScrollbar;
            },
        },

        watch: {
            selectedComponent() {
                this.updateFocusedButtonIndex();
            },
        },

        methods: {
            updateTabIndexes() {
                const listWrapper = document.querySelector('.selection-card-list');
                const buttons = Array.from(listWrapper.getElementsByClassName('selection-card'));
                const focusedButton = buttons[this.focusedButtonIndex];

                buttons?.forEach((button) => {
                    button.setAttribute('tabindex', -1);
                });

                if (focusedButton) {
                    focusedButton.setAttribute('tabindex', 0);
                }
            },

            updateFocusedButtonIndex() {
                const selectedComponentCode = this.selectedComponent.code;
                const listWrapper = document.querySelector('.selection-card-list');
                const selectedOption = listWrapper.querySelector(`[data-code='${selectedComponentCode}']`);
                const index = Array.prototype.indexOf.call(listWrapper.children, selectedOption);

                this.focusedButtonIndex = index;
                this.updateTabIndexes();
            },

            focusOnFirstOption() {
                const listWrapper = document.querySelector('.selection-card-list');
                const firstOption = listWrapper.firstChild;

                firstOption?.focus();
            },

            addKeyDownListener() {
                if (!this.hasKeyEventListener) {
                    this.$refs.selectionCardList.addEventListener('keydown', this.captureNavKeyPress);
                    this.hasKeyEventListener = true;
                }
            },

            removeKeyDownListener() {
                this.$refs.selectionCardList.removeEventListener('keydown', this.captureNavKeyPress);
                this.hasKeyEventListener = false;
            },

            captureNavKeyPress(event) {
                const buttonsWrapper = this.$refs.selectionCardList;
                const buttons = buttonsWrapper.getElementsByClassName('selection-card');
                const firstOption = buttons[0];
                const lastOption = buttons[buttons.length - 1];
                const focusedOptionElement = buttons[this.focusedButtonIndex];

                if (!document.activeElement.classList.contains('cz2__swiper-vertical')) {
                    if (event.keyCode === 40 || event.keyCode === 39) {
                        // Down or right arrow key
                        event.preventDefault();
                        event.stopPropagation();

                        if (focusedOptionElement && buttons[this.focusedButtonIndex + 1]) {
                            this.focusedButtonIndex += 1;
                            this.updateTabIndexes();
                            buttons[this.focusedButtonIndex].focus();
                        } else {
                            this.focusedButtonIndex = 0;
                            this.updateTabIndexes();
                            firstOption.focus();
                        }
                    } else if (event.keyCode === 38 || event.keyCode === 37) {
                        // Up or left arrow key
                        event.preventDefault();
                        event.stopPropagation();

                        if (focusedOptionElement && buttons[this.focusedButtonIndex - 1]) {
                            this.focusedButtonIndex -= 1;
                            this.updateTabIndexes();
                            buttons[this.focusedButtonIndex].focus();
                        } else {
                            this.focusedButtonIndex = buttons.length - 1;
                            this.updateTabIndexes();
                            lastOption.focus();
                        }
                    }
                }
            },

            focusChanged() {
                // Make the selected card the only focusable card after leaving the list and re-focusing on the list
                if (!this.$refs.selectionCardList.contains(document.activeElement)) {
                    this.focusedButtonIndex = this.selectedButtonIndex;
                    this.updateTabIndexes();
                }
            },
        },
    };
</script>
